import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DemoVideo = ({ isOpen, onClose }) => {
    const [isPlaying, setIsPlaying] = useState(true);
    const [currentScene, setCurrentScene] = useState(0);
    const [progress, setProgress] = useState(0);
    
    const scenes = [
        {
            title: "Welcome to RestroAI",
            subtitle: "AI-Powered Restaurant Management Platform",
            description: "Transform your restaurant operations with cutting-edge artificial intelligence",
            icon: "🚀",
            color: "from-blue-500 to-cyan-500",
            duration: 4000
        },
        {
            title: "Smart Dashboard",
            subtitle: "Real-Time Analytics & Insights",
            description: "Monitor sales, orders, and performance with live data visualization and AI-powered recommendations",
            icon: "📊",
            color: "from-purple-500 to-pink-500",
            duration: 5000
        },
        {
            title: "Intelligent Menu Management",
            subtitle: "AI-Optimized Menu & Pricing",
            description: "Automatically optimize menu items, pricing, and inventory based on customer preferences and sales data",
            icon: "🍽️",
            color: "from-green-500 to-emerald-500",
            duration: 5000
        },
        {
            title: "Customer Analytics",
            subtitle: "Deep Customer Insights",
            description: "Understand customer behavior, preferences, and ordering patterns with advanced AI analytics",
            icon: "👥",
            color: "from-orange-500 to-red-500",
            duration: 5000
        },
        {
            title: "Order Management",
            subtitle: "Streamlined Operations",
            description: "Manage orders efficiently with real-time tracking, kitchen optimization, and automated workflows",
            icon: "📋",
            color: "from-indigo-500 to-purple-500",
            duration: 5000
        },
        {
            title: "Revenue Optimization",
            subtitle: "Maximize Your Profits",
            description: "Use predictive analytics for demand forecasting, dynamic pricing, and profit maximization strategies",
            icon: "💰",
            color: "from-yellow-500 to-orange-500",
            duration: 5000
        },
        {
            title: "Multi-Platform Access",
            subtitle: "Anywhere, Anytime",
            description: "Access your restaurant data seamlessly across web, mobile, and tablet devices with cloud synchronization",
            icon: "📱",
            color: "from-teal-500 to-blue-500",
            duration: 4000
        },
        {
            title: "Get Started Today!",
            subtitle: "Join 500+ Successful Restaurants",
            description: "Start your free trial now and experience the future of restaurant management with RestroAI",
            icon: "✨",
            color: "from-pink-500 to-purple-500",
            duration: 4000
        }
    ];
    
    const totalDuration = scenes.reduce((sum, scene) => sum + scene.duration, 0);

    // Scene progression logic
    useEffect(() => {
        if (!isPlaying || !isOpen) return;
        
        const currentDuration = scenes[currentScene]?.duration || 4000;
        
        const sceneTimer = setTimeout(() => {
            setCurrentScene(prev => {
                const nextScene = prev + 1;
                return nextScene >= scenes.length ? 0 : nextScene;
            });
            setProgress(0);
        }, currentDuration);
        
        const progressTimer = setInterval(() => {
            setProgress(prev => {
                const increment = 100 / (currentDuration / 50);
                const newProgress = prev + increment;
                return newProgress >= 100 ? 100 : newProgress;
            });
        }, 50);
        
        return () => {
            clearTimeout(sceneTimer);
            clearInterval(progressTimer);
        };
    }, [currentScene, isPlaying, isOpen]);
    
    // Auto-start when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentScene(0);
            setProgress(0);
            setIsPlaying(true);
        } else {
            setIsPlaying(false);
        }
    }, [isOpen]);
    
    const getCurrentTime = () => {
        let elapsed = 0;
        for (let i = 0; i < currentScene; i++) {
            elapsed += scenes[i].duration;
        }
        elapsed += (progress / 100) * scenes[currentScene].duration;
        return Math.floor(elapsed / 1000);
    };
    
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-6 lg:p-8 max-w-6xl w-full max-h-[98vh] sm:max-h-[90vh] overflow-hidden relative"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white/60 hover:text-white text-xl sm:text-2xl z-10 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/40 transition-all"
                    >
                        ✕
                    </button>

                    {/* Demo Video Container */}
                    <div className="aspect-video bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-md sm:rounded-lg lg:rounded-xl relative overflow-hidden mb-3 sm:mb-6">
                        {/* Animated Background */}
                        <div className="absolute inset-0">
                            {[...Array(30)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-1 h-1 bg-white rounded-full opacity-20"
                                    initial={{
                                        x: Math.random() * 100 + '%',
                                        y: Math.random() * 100 + '%',
                                    }}
                                    animate={{
                                        x: Math.random() * 100 + '%',
                                        y: Math.random() * 100 + '%',
                                        opacity: [0.2, 0.8, 0.2],
                                    }}
                                    transition={{
                                        duration: Math.random() * 20 + 10,
                                        repeat: Infinity,
                                        ease: "linear",
                                    }}
                                />
                            ))}
                        </div>
                        
                        {/* Scene Content */}
                        <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-6 lg:p-8">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentScene}
                                    initial={{ opacity: 0, scale: 0.8, y: 30 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.8, y: -30 }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    className="text-center w-full max-w-5xl"
                                >
                                    <motion.div
                                        className={`text-3xl sm:text-5xl lg:text-7xl mb-2 sm:mb-4 lg:mb-6 bg-gradient-to-r ${scenes[currentScene].color} bg-clip-text text-transparent`}
                                        animate={{
                                            scale: [1, 1.05, 1],
                                            rotate: [0, 3, -3, 0]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        {scenes[currentScene].icon}
                                    </motion.div>
                                    
                                    <motion.h2
                                        className="text-lg sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 leading-tight px-1"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2, duration: 0.5 }}
                                    >
                                        {scenes[currentScene].title}
                                    </motion.h2>
                                    
                                    <motion.h3
                                        className={`text-sm sm:text-lg md:text-xl lg:text-2xl font-semibold mb-2 sm:mb-4 lg:mb-6 bg-gradient-to-r ${scenes[currentScene].color} bg-clip-text text-transparent leading-tight px-1`}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4, duration: 0.5 }}
                                    >
                                        {scenes[currentScene].subtitle}
                                    </motion.h3>
                                    
                                    <motion.p
                                        className="text-xs sm:text-sm md:text-base lg:text-lg text-white/80 leading-snug sm:leading-relaxed max-w-4xl mx-auto px-2 sm:px-4"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6, duration: 0.5 }}
                                    >
                                        {scenes[currentScene].description}
                                    </motion.p>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-2 bg-black/30">
                            <motion.div
                                className={`h-full bg-gradient-to-r ${scenes[currentScene].color}`}
                                style={{ width: `${progress}%` }}
                                transition={{ duration: 0.1 }}
                            />
                        </div>
                        
                        {/* Scene Counter */}
                        <div className="absolute top-1 left-1 sm:top-3 sm:left-3 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5 text-white text-xs font-medium">
                            {currentScene + 1}/{scenes.length}
                        </div>
                        
                        {/* Time Display */}
                        <div className="absolute top-1 right-10 sm:top-3 sm:right-14 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1.5 text-white text-xs font-medium">
                            {formatTime(getCurrentTime())}/{formatTime(Math.floor(totalDuration / 1000))}
                        </div>
                    </div>

                    {/* Video Info */}
                    <div className="text-center">
                        <p className="text-white/70 text-xs sm:text-sm px-2 leading-tight">
                            RestroAI Demo - AI-Powered Restaurant Management
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default DemoVideo;